import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { useLocation } from 'react-router'
import classnames from 'classnames'
import { getAriaLabel } from 'shared/lib'
import { Typography } from 'shared/ui/Typography'
import { IconButton } from 'shared/ui/IconButton'
import { type IIconProps } from 'shared/ui/Icon/Icon'
import { type ITooltipProps } from 'shared/ui/Tooltip/ui/Tooltip'
import { ZIndex } from 'shared/constants/zIndex'
import modalFullScreenStore from './store/modalFullScreenStore'
import styles from './styles.module.scss'

type ICollapseButtonProps = {
  iconProps: IIconProps
  tooltipProps?: ITooltipProps
  onClick: () => void
}

type IModalFullScreenProps = {
  collapseButton?: ICollapseButtonProps
}

export const ModalFullScreen = observer(({ collapseButton }: IModalFullScreenProps) => {
  const { modals, closeModal, closeAllModals } = modalFullScreenStore
  const location = useLocation()

  useEffect(() => {
    closeAllModals()
  }, [location.pathname])

  const onClose = (id: string) => {
    closeModal(id)
  }

  return (
    <>
      {modals.map((modal) => {
        const { title, id, ModalContent, ModalContentProps } = modal
        const ariaLabelRoot = 'ModalFullScreen'

        return (
          <div
            key={modal.id}
            className={styles.wrap}
            aria-label={getAriaLabel(ariaLabelRoot, String(modal.title))}
            style={{ zIndex: modal.zIndex || ZIndex.OVERLAY_BASE }}
          >
            <div className={styles.wrapContent}>
              <div
                className={classnames(
                  styles.content,
                  { [styles.noHeader]: !title },
                  modal.className
                )}
                style={{ maxWidth: modal.maxWidth || '532px' }}
              >
                {title && (
                  <div className={styles.header} aria-label={getAriaLabel(ariaLabelRoot, 'header')}>
                    <div
                      className={styles.title}
                      aria-label={getAriaLabel(ariaLabelRoot, 'header_title')}
                    >
                      <Typography variant={'body-xs-regular'} ariaLabel={'title'}>
                        {title}
                      </Typography>
                    </div>
                  </div>
                )}
                {ModalContent && <ModalContent {...ModalContentProps} />}
              </div>
              {collapseButton && (
                <div className={styles.collapseButton}>
                  <IconButton
                    iconProps={{
                      fontSize: collapseButton.iconProps.fontSize,
                      icon: collapseButton.iconProps.icon,
                    }}
                    tooltipProps={collapseButton.tooltipProps}
                    variant={'icon'}
                    size={'medium'}
                    color={'secondary'}
                    onClick={collapseButton.onClick}
                    ariaLabel={getAriaLabel(ariaLabelRoot, 'collapse')}
                  />
                </div>
              )}
              <div className={styles.closeButton}>
                <IconButton
                  icon={'close'}
                  size={'medium'}
                  color={'secondary'}
                  variant={'icon'}
                  onClick={() => onClose(id)}
                  ariaLabel={getAriaLabel(ariaLabelRoot, 'close')}
                />
              </div>
            </div>
          </div>
        )
      })}
    </>
  )
})
